// src/pages/Dashboard/teacher/paperEvaluation/PaperEvaluationModal.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const PaperEvaluationModal = ({
  isOpen,
  onClose,
  onSubmit,
  ocrText,
  questionMeta,
  isSubmitting,
}) => {
  const { t } = useTranslation();

  const [questionText, setQuestionText] = useState("");
  const [maxMarks, setMaxMarks] = useState("");
  const [keywordsInput, setKeywordsInput] = useState("");

  useEffect(() => {
    if (isOpen) {
      setQuestionText("");
      setMaxMarks("");
      setKeywordsInput("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();

    const marksNum = parseInt(maxMarks, 10);
    if (Number.isNaN(marksNum) || marksNum <= 0) {
      alert(t("paperEval.alertMarks"));
      return;
    }

    const keywords = keywordsInput
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);

    if (keywords.length === 0) {
      alert(t("paperEval.alertKeywords"));
      return;
    }

    onSubmit({
      questionText: questionText || null,
      maxMarks: marksNum,
      keywords,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t("paperEval.verifyGrade")}
            </h3>

            {questionMeta && (
              <p className="text-xs text-gray-500 mt-0.5">
                {questionMeta.section_type} • {questionMeta.question_id}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-hidden flex flex-col sm:flex-row"
        >
          {/* OCR text */}
          <div className="sm:w-1/2 border-b sm:border-b-0 sm:border-r border-gray-200 p-4 sm:p-5 flex flex-col">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">
              {t("paperEval.stitchedText")}
            </h4>

            <textarea
              value={ocrText || ""}
              readOnly
              className="flex-1 w-full text-xs sm:text-sm p-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700 resize-none"
            />
          </div>

          {/* Rubric form */}
          <div className="sm:w-1/2 p-4 sm:p-5 flex flex-col gap-3">

            {/* Question text */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t("paperEval.questionOptional")}
              </label>
              <textarea
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                placeholder={t("paperEval.questionOptionalPlaceholder")}
                className="w-full text-xs sm:text-sm p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={3}
              />
            </div>

            {/* Marks + keywords */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t("paperEval.maxMarks")}
                </label>
                <input
                  type="number"
                  min="1"
                  value={maxMarks}
                  onChange={(e) => setMaxMarks(e.target.value)}
                  placeholder="e.g., 5"
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t("paperEval.keywords")}
                </label>
                <input
                  type="text"
                  value={keywordsInput}
                  onChange={(e) => setKeywordsInput(e.target.value)}
                  placeholder="e.g., evaporation, heat"
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Footer buttons */}
            <div className="mt-auto pt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {t("paperEval.cancel")}
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400"
              >
                {isSubmitting
                  ? t("paperEval.evaluating")
                  : t("paperEval.evaluateBtn")}
              </button>
            </div>

          </div>
        </form>

      </div>
    </div>
  );
};

export default PaperEvaluationModal;
